import { useTranslation } from "react-i18next";
import { ArrowUpRight, Loader2, Plus, Settings2 } from "@digital-twins/design-system/icons";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { Badge } from "@/components/ui/badge";
import type { CreativeWorkspaceHeaderProps } from "@/types/creative-library";

export function CreativeWorkspaceHeader({
  boardToken,
  board,
  status,
  disabled,
  starting,
  onSettings,
  onCreate,
  onUpload,
}: CreativeWorkspaceHeaderProps) {
  const { t } = useTranslation("app");
  const title =
    board?.name ??
    t(
      boardToken
        ? "creativeLibrary.boardUnavailable"
        : "creativeLibrary.creatives",
    );
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 flex-col gap-1.5">
        <Heading
          level={2}
          size="xl"
          className="break-words"
          data-testid="creative-workspace-title"
        >
          {boardToken && status === "loading"
            ? t("creativeLibrary.loading")
            : title}
        </Heading>
        <Text size="sm" color="muted" className="max-w-xl">
          {board?.description ||
            t(
              boardToken
                ? "creativeLibrary.boardWorkspaceHint"
                : "creativeLibrary.creativesHint",
            )}
        </Text>
        {board && (
          <div className="flex flex-wrap gap-1.5">
            {!board.canEdit && (
              <Badge variant="outline">
                {t("creativeLibrary.boardViewOnly")}
              </Badge>
            )}
            {board.channelTags.map((tag) => (
              <Badge key={tag} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {boardToken && board ? (
          <>
            {board.canEdit && (
              <Button
                variant="outline"
                className="gap-2 px-3 sm:px-4"
                onClick={onSettings}
                data-testid="creative-board-settings"
              >
                <Settings2 className="h-4 w-4" />
                {t("creativeLibrary.boardSettings")}
              </Button>
            )}
            <Button
              variant="primary"
              className="gap-2 px-3 sm:px-4"
              onClick={onCreate}
              disabled={disabled}
              data-testid="creative-use-board"
            >
              {starting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUpRight className="h-4 w-4" />
              )}
              {t("creativeLibrary.createWithBoard")}
            </Button>
          </>
        ) : (
          !boardToken && (
            <Button
              variant="primary"
              className="gap-2 px-3 sm:px-4"
              onClick={onUpload}
              data-testid="creative-new-creatives"
            >
              <Plus className="h-4 w-4" />
              {t("creativeLibrary.uploadReference")}
            </Button>
          )
        )}
      </div>
    </div>
  );
}
